import classNames from 'classnames'
import { JSX, useEffect, useMemo, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { Table, tableTypes, tableUtils } from 'core-components/library/list'
import { Loader } from 'core-components/library/loader'
import styles from 'core-components/library/menu/SubMenu.module.scss'
import {
  AbsolutePopup,
  IPopupContentData,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'
import { BaseCrud, Static } from 'core-components/types'

export interface IItem extends IForm {
  id: string
  caption: string
  parentId: string
  hasChildren: boolean
  roles: string[]
}

export interface ISubMenuProps<T = IForm, R = string> {
  data: any[]
  isDataLoading: boolean
  source: BaseCrud<any>
  root: string
  hoveredRows: { [key: string]: string }
  setHoveredRows: (newValue: { [key: string]: string }) => void
  onChoose?: (id: R) => void
  caption?: string
  dataPreprocessPredicate?: (item: any) => boolean
  openNode?: (value: string) => Promise<any>
  onReady?: () => void
  subMenuClasses?: {
    row?: string
    cell?: string
    iconCell?: string
    popPaper?: string
    popupWrap?: string
    selectedItem?: string
  }
  subMenuPopupOptions?: {
    horizontalStartPosition?: TPopupHorizontal
    horizontalDirection?: TPopupHorizontal
    verticalStartPosition?: TPopupVertical
    verticalDirection?: TPopupVertical
  }
  itemTpl?: (item: any) => JSX.Element
  hierarchyField?: string
  subMenuTrigger?: TVisibilityTrigger
}

export const SubMenu = <T = IForm, R = string>(props: ISubMenuProps<T, R>) => {
  const {
    data,
    source,
    root,
    hierarchyField,
    itemTpl,
    subMenuTrigger,
    onReady,
    isDataLoading,
    subMenuPopupOptions,
  } = props

  const [visibleSubMenus, setVisibleSubMenus] = useState<string[]>([])
  const [loadingSubMenus, setLoadingSubMenus] = useState<string[]>([])
  const [rowLoadingIndex, setRowLoadingIndex] = useState<number | undefined>(0)

  const [isDataRender, setIsDataRender] = useState<boolean>(false)

  const [isReady, setIsReady] = useState<boolean>(false)
  const keyProperty = source.keyField || 'id'
  const dataRender = useMemo(() => {
    let filteredData = data
    if (props.dataPreprocessPredicate) {
      filteredData = data.filter(props.dataPreprocessPredicate)
    }
    return filteredData.filter((el: any) => {
      if (source.hierarchyField) {
        return el[source.hierarchyField] === root
      }
      return !root
    })
  }, [data, props.dataPreprocessPredicate, root, source.hierarchyField])

  const newSource = useMemo(() => {
    return dataRender.length
      ? new BaseCrud<IItem>({
          keyField: keyProperty,
          fetcher: new Static(dataRender),
          defaultPaging: {
            page: 0,
            pageSize: dataRender.length,
          },
        })
      : null
  }, [dataRender, keyProperty])

  const isVisible = (item: IItem) => {
    const key = item[keyProperty]
    return visibleSubMenus.includes(key) || loadingSubMenus.includes(key)
  }

  const openNode = async (e: any, item: IItem) => {
    if (props.openNode && !isVisible(item)) {
      e.preventDefault()
      e.stopPropagation()
      setRowLoadingIndex(item[keyProperty])
      await props.openNode(item[keyProperty])
      if (!loadingSubMenus.includes(item[keyProperty])) {
        setLoadingSubMenus([...loadingSubMenus, item[keyProperty]])
      }
      setRowLoadingIndex(0)
    }
  }

  useEffect(() => {
    if (newSource && !isReady) {
      setIsReady(true)
      if (onReady) {
        onReady()
      }
    }
  }, [newSource, isReady, onReady])

  if (!newSource) {
    return null
  }

  return (
    <Table
      classes={{
        root: classNames(styles.list, !isDataRender ? 'content-invisible' : ''),
        scrollRoot: styles.list__scrollRoot,
      }}
      mode={tableTypes.TableMode.div}
      onClick={(item: IItem) => {
        if (props.onChoose) {
          props.onChoose(item[keyProperty])
        }
      }}
      columns={[
        {
          title: props.caption || 'caption',
          field: props.caption || 'caption',
        },
        { title: 'hasChildren', field: 'hasChildren' },
      ]}
      source={newSource}
      onDataLoad={() => setIsDataRender(true)}
      rowTpl={(item: IItem, renderChildren, rowState, rowAttrs, additional) => {
        const hasChildren = tableUtils.getValue(item, hierarchyField || 'hasChildren')
        const popupContent = (popupData: IPopupContentData) => {
          const subMenuDefaultPopupOptions = {
            horizontalDirection: popupData.direction.horizontal,
            verticalDirection: popupData.direction.vertical,
            horizontalStartPosition: popupData.direction.horizontal,
            verticalStartPosition: popupData.startPosition.vertical,
          }

          const newSubMenuPopupOptions = Object.keys(subMenuDefaultPopupOptions).reduce(
            (acc, key) => {
              if (subMenuPopupOptions?.hasOwnProperty(key) && !!subMenuPopupOptions[key]) {
                return { ...acc, [key]: subMenuPopupOptions[key] }
              }
              return { ...acc, [key]: subMenuDefaultPopupOptions[key] }
            },
            {}
          )

          return (
            <PopPaper className={classNames(styles.popup, props.subMenuClasses?.popPaper)}>
              <SubMenu
                isDataLoading={isDataLoading}
                subMenuPopupOptions={newSubMenuPopupOptions}
                onChoose={props.onChoose}
                hierarchyField={hierarchyField}
                data={data}
                itemTpl={itemTpl}
                source={source}
                root={item[keyProperty]}
                caption={props.caption}
                subMenuTrigger={subMenuTrigger}
                dataPreprocessPredicate={props.dataPreprocessPredicate}
                openNode={props.openNode}
                subMenuClasses={props.subMenuClasses}
                hoveredRows={props.hoveredRows}
                setHoveredRows={props.setHoveredRows}
                onReady={() => {
                  if (!visibleSubMenus.includes(item[keyProperty])) {
                    setVisibleSubMenus([...visibleSubMenus, item[keyProperty]])
                  }
                }}
              />
            </PopPaper>
          )
        }
        const target = (
          <div
            {...rowAttrs}
            key={rowAttrs.key}
            data-testid={`submenu-item-row-${item[keyProperty]}`}
            className={classNames(rowAttrs.className, styles.row, props.subMenuClasses?.row)}
            onMouseOver={async (e) => {
              const newValue = { ...props.hoveredRows, [root]: item[keyProperty] }
              props.setHoveredRows(newValue)

              if (subMenuTrigger === TVisibilityTrigger.Hover && hasChildren) {
                await openNode(e, item)
              }
            }}
            onMouseLeave={() => {
              props.setHoveredRows({ ...props.hoveredRows, [root]: '' })
            }}
          >
            {renderChildren(item, rowState, additional)}
          </div>
        )
        const horizontalDirection =
          subMenuPopupOptions?.horizontalDirection || TPopupHorizontal.Right
        const horizontalStartPosition =
          subMenuPopupOptions?.horizontalStartPosition || horizontalDirection
        const verticalDirection = subMenuPopupOptions?.verticalDirection || TPopupVertical.Bottom
        const verticalStartPosition =
          subMenuPopupOptions?.verticalStartPosition || TPopupVertical.Top

        return (
          <AbsolutePopup
            popupClassName={classNames(styles.popupWrap, props.subMenuClasses?.popupWrap || '')}
            verticalDirection={verticalDirection}
            verticalStartPosition={verticalStartPosition}
            horizontalStartPosition={horizontalStartPosition}
            horizontalDirection={horizontalDirection}
            visibilityCallback={() => {
              return props.hoveredRows[root] === item[keyProperty] && isVisible(item) && !!newSource
            }}
            popupContent={popupContent}
          >
            {target}
          </AbsolutePopup>
        )
      }}
      cellTpl={(item: IItem, column, cellAttrs) => {
        const hasChildren = tableUtils.getValue(item, hierarchyField || 'hasChildren')
        const isHierarchyCell = column.field === 'hasChildren'
        return (
          <div
            {...cellAttrs}
            key={cellAttrs.key}
            className={classNames(
              cellAttrs.className,
              styles.cell,
              props.subMenuClasses && props.subMenuClasses.cell,
              isHierarchyCell ? styles.cell_icon : '',
              (isHierarchyCell && props.subMenuClasses && props.subMenuClasses.iconCell) || ''
            )}
            data-testid={
              isHierarchyCell
                ? `submenu-item-hierarchy-cell-${item[keyProperty]}`
                : `submenu-item-${item[keyProperty]}`
            }
          >
            {column.field === 'hasChildren' ? (
              hasChildren ? (
                item[keyProperty] === rowLoadingIndex ? (
                  <div className={styles.loaderWrapper}>
                    <Loader dimension={{ width: 20, height: 20 }} />
                  </div>
                ) : (
                  <div className={styles.icon} data-testid={`submenu-item-icon-${item[keyProperty]}`} onClick={async (e) => await openNode(e, item)}>
                    <i className={'kbase kbase-omni-chevron-right'} data-name={'SelectIcon'} />
                  </div>
                )
              ) : (
                ''
              )
            ) : itemTpl ? (
              itemTpl(item)
            ) : (
              `${item[column.field]}`
            )}
          </div>
        )
      }}
      useHotKeys
    />
  )
}
